import React, { useState } from "react";
import { FaEnvelope, FaMapMarkerAlt, FaLinkedin, FaComments, FaHeadset, FaPaperPlane } from "react-icons/fa";
import { FaXTwitter } from "react-icons/fa6";
import analytics from "../services/analytics";
import { showToast } from "./ToastNotification";
import { sendContactEmail } from "../services/emailService";

const ContactSection = () => {
    const [formData, setFormData] = useState({
        name: '',
        email: '',
        topic: '',
        message: ''
    });
    const [isSubmitting, setIsSubmitting] = useState(false);

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!formData.name || !formData.email || !formData.message) {
            showToast('Please fill in all required fields', 'error');
            analytics.trackFormSubmit('home_contact', false);
            return;
        }

        setIsSubmitting(true);

        // Track form submission attempt
        analytics.event('Contact', 'form_submitted', 'home_page', null, {
            topic: formData.topic || 'Not selected',
            message_length: formData.message.length
        });

        try {
            // Send email via API
            await sendContactEmail(formData, 'home', 'info@riskdiscover.com');

            analytics.trackFormSubmit('home_contact', true);
            analytics.event('Contact', 'email_sent_success', 'home_page');

            showToast('Message sent successfully! We\'ll get back to you within 24 hours.', 'success');

            // Reset form
            setFormData({ name: '', email: '', topic: '', message: '' });
        } catch (error) {
            console.error('Error sending message:', error);
            analytics.trackFormSubmit('home_contact', false);
            analytics.event('Contact', 'email_send_failed', 'home_page');

            showToast('Failed to send message. Please try again or email us directly at info@riskdiscover.com', 'error');
        } finally {
            setIsSubmitting(false);
        }
    };
    
    return (
        <section className="py-24 bg-gradient-to-b from-white to-gray-50">
            <div className="max-w-7xl mx-auto px-6 lg:px-8">
                <div className="grid lg:grid-cols-2 gap-16 items-center">
                    {/* Contact Information */}
                    <div>
                        <div className="inline-flex items-center justify-center p-2 bg-blue-100 rounded-full mb-6">
                            <FaComments className="text-blue-600 text-xl" />
                        </div>
                        
                        <h2 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-6">
                            Let's Start a Conversation
                        </h2>
                        <p className="text-xl text-gray-600 mb-10">
                            Have questions about RiskDiscover? Our expert team is ready to help you transform your risk assessment process.
                        </p>
                        
                        {/* Contact Cards */}
                        <div className="space-y-4">
                            <div className="flex items-center p-4 bg-white rounded-xl shadow-md hover:shadow-lg transition-shadow">
                                <div className="bg-gradient-to-br from-blue-500 to-blue-600 rounded-lg p-3 mr-4">
                                    <FaEnvelope className="text-white text-xl" />
                                </div>
                                <div>
                                    <p className="text-sm text-gray-500">Email Us</p>
                                    <a href="mailto:info@riskdiscover.com" className="text-lg font-semibold text-gray-900 hover:text-blue-600 transition-colors">
                                        info@riskdiscover.com
                                    </a>
                                </div>
                            </div>
                            
                            <div className="flex items-center p-4 bg-white rounded-xl shadow-md hover:shadow-lg transition-shadow">
                                <div className="bg-gradient-to-br from-green-500 to-green-600 rounded-lg p-3 mr-4">
                                    <FaHeadset className="text-white text-xl" />
                                </div>
                                <div>
                                    <p className="text-sm text-gray-500">Support Hours</p>
                                    <p className="text-lg font-semibold text-gray-900">
                                        Mon-Fri 9AM-6PM CET
                                    </p>
                                </div>
                            </div>
                            
                            <div className="flex items-center p-4 bg-white rounded-xl shadow-md hover:shadow-lg transition-shadow">
                                <div className="bg-gradient-to-br from-purple-500 to-purple-600 rounded-lg p-3 mr-4">
                                    <FaMapMarkerAlt className="text-white text-xl" />
                                </div>
                                <div>
                                    <p className="text-sm text-gray-500">Location</p>
                                    <p className="text-lg font-semibold text-gray-900">
                                        European Business District
                                    </p>
                                </div>
                            </div>
                        </div>
                        
                        {/* Social Links */}
                        <div className="mt-10">
                            <p className="text-sm text-gray-600 mb-4">Connect with us</p>
                            <div className="flex space-x-3">
                                <a 
                                    href="https://www.linkedin.com/company/moredevs" 
                                    target="_blank" 
                                    rel="noopener noreferrer"
                                    onClick={() => analytics.trackButtonClick('linkedin_social', 'home_contact')}
                                    className="bg-white rounded-lg p-3 shadow-md hover:shadow-lg transition-all hover:-translate-y-1"
                                >
                                    <FaLinkedin className="text-blue-600 text-xl" />
                                </a>
                                <a 
                                    href="https://x.com/riskdiscover" 
                                    target="_blank" 
                                    rel="noopener noreferrer"
                                    onClick={() => analytics.trackButtonClick('twitter_social', 'home_contact')}
                                    className="bg-white rounded-lg p-3 shadow-md hover:shadow-lg transition-all hover:-translate-y-1"
                                >
                                    <FaXTwitter className="text-gray-900 text-xl" />
                                </a>
                            </div>
                        </div>
                    </div>
                    
                    {/* Modern Contact Form Design */}
                    <div className="relative">
                        {/* Background decoration */}
                        <div className="absolute inset-0 bg-gradient-to-br from-blue-600 to-indigo-700 rounded-3xl transform rotate-3"></div>
                        
                        {/* Form Card */}
                        <div className="relative bg-white rounded-3xl shadow-2xl p-8">
                            <h3 className="text-2xl font-bold text-gray-900 mb-6">
                                Quick Message
                            </h3>
                            
                            <form onSubmit={handleSubmit} className="space-y-4">
                                <div>
                                    <input 
                                        type="text" 
                                        name="name"
                                        value={formData.name}
                                        onChange={handleInputChange}
                                        placeholder="Your Name"
                                        className="w-full px-4 py-3 rounded-lg border border-gray-200 focus:border-blue-500 focus:outline-none transition-colors"
                                        required
                                    />
                                </div>
                                <div>
                                    <input 
                                        type="email" 
                                        name="email"
                                        value={formData.email}
                                        onChange={handleInputChange}
                                        placeholder="Your Email"
                                        className="w-full px-4 py-3 rounded-lg border border-gray-200 focus:border-blue-500 focus:outline-none transition-colors"
                                        required
                                    />
                                </div>
                                <div>
                                    <select 
                                        name="topic"
                                        value={formData.topic}
                                        onChange={handleInputChange}
                                        className="w-full px-4 py-3 rounded-lg border border-gray-200 focus:border-blue-500 focus:outline-none transition-colors text-gray-600"
                                    >
                                        <option value="">Select Topic</option>
                                        <option value="Product Demo">Product Demo</option>
                                        <option value="Pricing">Pricing</option>
                                        <option value="Technical Support">Technical Support</option>
                                        <option value="Partnership">Partnership</option>
                                    </select>
                                </div>
                                <div>
                                    <textarea 
                                        name="message"
                                        value={formData.message}
                                        onChange={handleInputChange}
                                        placeholder="Your Message"
                                        rows="4"
                                        className="w-full px-4 py-3 rounded-lg border border-gray-200 focus:border-blue-500 focus:outline-none transition-colors resize-none"
                                        required
                                    ></textarea>
                                </div>
                                
                                <button
                                    type="submit"
                                    disabled={isSubmitting}
                                    className="w-full bg-gradient-to-r from-blue-600 to-indigo-600 text-white py-3 px-6 rounded-lg font-semibold hover:shadow-lg transform hover:scale-105 transition-all flex items-center justify-center disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none"
                                >
                                    {isSubmitting ? 'Sending...' : 'Send Message'}
                                    <FaPaperPlane className="ml-2" />
                                </button>
                            </form>
                            
                            <p className="text-xs text-gray-500 text-center mt-6">
                                We'll respond within 24 hours
                            </p>
                        </div>
                    </div>
                </div>
                
                {/* Bottom CTA */}
                <div className="mt-20 text-center bg-gradient-to-r from-gray-900 to-gray-800 rounded-2xl p-10">
                    <h3 className="text-2xl font-bold text-white mb-4">
                        Prefer to Schedule a Demo?
                    </h3>
                    <p className="text-gray-300 mb-6">
                        Book a personalized walkthrough with our product experts
                    </p>
                    <a
                        href="http://bit.ly/43sbWRD"
                        target="_blank"
                        rel="noopener noreferrer"
                        onClick={() => analytics.trackButtonClick('schedule_demo', 'home_contact')}
                        className="inline-block bg-white text-gray-900 px-8 py-3 rounded-lg font-semibold hover:shadow-lg transition-all transform hover:scale-105"
                    >
                        Schedule Demo
                    </a>
                </div>
            </div>
        </section>
    );
};

export default ContactSection;